import { EquipSlot, ItemDropTarget, ItemDetailMeta, ItemDetailOption } from "game-common/models"
import { Container, Graphics, InteractionEvent, InteractionManager, Point, Rectangle, Ticker } from "pixi.js"

/** How a gear socket looks while something is being dragged: the one it fits, the rest, or a refusal. */
export type SocketPrime = "want" | "dim" | "bad"

/**
 * A gear socket takes only its own kind of item (the server stamps each item's `equipSlot`). Anything
 * else let go of over it bounces back. `worn` says its occupant is worn gear, which a replacement swaps
 * places with (the sealed slot option); an occupant shown from the ready bar is just put away.
 */
export interface SocketOptions {
    kind: EquipSlot
    worn?: boolean
    prime: (state?: SocketPrime) => void
    reject?: () => void
}

interface Slot {
    node: Graphics
    item?: ItemDetailMeta
    section: ItemDropTarget
    size: number
    highlight: (on: boolean) => void
    socket?: SocketOptions
}

const BOUNCE_MS = 220

interface Section {
    node: Container
    bounds: Rectangle
    highlighted: boolean
    section: ItemDropTarget
    highlight: (on: boolean) => void
}

/** Dispatch only supplied actions; inventory dialogs supply server-minted options. */
export class ItemSlotDrag {
    private slots: Slot[] = []
    private sections: Section[] = []
    private held?: { source: Slot; pointerId: number; start: Point; moved: boolean }
    private ghost?: Container
    private suppressed?: Graphics

    constructor(
        private interaction: InteractionManager,
        private layer: Container,
        private makeGhost: (item: ItemDetailMeta, size: number) => Container,
        // `at`: the screen point it was let go of, given when it was let go of over the world.
        private select: (item: ItemDetailMeta, option: ItemDetailOption, at?: Point) => void,
        private hideDetails: () => void,
        private isWorldDrop: (point: Point) => boolean = () => false,
    ) {}

    registerSection = (node: Container, bounds: Rectangle, section: ItemDropTarget,
        highlight: (on: boolean) => void) => {
        this.sections.push({ node, bounds, section, highlight, highlighted: false })
    }

    registerTarget = (node: Graphics, item: ItemDetailMeta | undefined, section: ItemDropTarget,
        size: number, highlight: (on: boolean) => void, socket?: SocketOptions) => {
        const target: Slot = { node, item, section, size, highlight, socket }
        this.slots.push(target)
        return target
    }

    register = (node: Graphics, item: ItemDetailMeta | undefined, section: ItemDropTarget,
        size: number, highlight: (on: boolean) => void, socket?: SocketOptions) => {
        const source = this.registerTarget(node, item, section, size, highlight, socket)
        // Empty wells must also consume presses so the panel's background does not drag.
        node.interactive = true
        if (!node.hitArea) node.hitArea = new Rectangle(0, 0, size, size)
        if (!item) return
        node.cursor = "grab"
        node.on("pointerdown", (event: InteractionEvent) => {
            if (this.held || (event.data.pointerType !== "touch" && event.data.button > 0)) return
            this.suppressed = undefined
            this.held = { source, pointerId: event.data.pointerId, start: event.data.global.clone(), moved: false }
            this.interaction.on("pointermove", this.move)
            this.interaction.on("pointerup", this.release)
            this.interaction.on("pointerupoutside", this.cancelPointer)
            this.interaction.on("pointercancel", this.cancelPointer)
            window.addEventListener("blur", this.cancel)
            event.stopPropagation()
        })
    }

    suppressClick = (node: Graphics) => this.suppressed === node
    get dragging() { return !!this.held?.moved }

    // Whether this socket turns the held item away: it is not that socket's kind of item.
    private refuses = (slot?: Slot) => !!slot?.socket && this.held?.source !== slot &&
        this.held?.source.item?.equipSlot !== slot.socket.kind

    private optionFor = (section: ItemDropTarget, slot?: Slot) => {
        const source = this.held?.source
        if (!source || source === slot) return undefined
        if (slot?.socket) {
            if (this.refuses(slot) || slot.item?.id === source.item?.id) return undefined
            // Replacing worn gear swaps the two over; anything else in the socket is simply put away.
            const swap = slot.socket.worn && slot.item?.id ? source.item?.itemSlotDropOptions?.[slot.item.id] : undefined
            return swap ?? source.item?.itemDropOptions?.equipped
        }
        if (slot?.item?.id && source.item?.itemSlotDropOptions?.[slot.item.id]) {
            return source.item.itemSlotDropOptions[slot.item.id]
        }
        if (source.section === section) return undefined
        return source.item?.itemDropOptions?.[section]
    }

    private slotAt = (event: InteractionEvent) => this.slots.find(slot => {
        const at = slot.node.toLocal(event.data.global)
        return slot.node.hitArea?.contains(at.x, at.y)
    })

    private targetAt = (event: InteractionEvent) => {
        const target = this.sections.find(section => {
            const at = section.node.toLocal(event.data.global)
            return section.bounds.contains(at.x, at.y)
        })
        return target?.section ?? this.slotAt(event)?.section
            ?? (this.isWorldDrop(event.data.global) ? "world" : undefined)
    }

    private move = (event: InteractionEvent) => {
        const held = this.held
        if (!held || held.pointerId !== event.data.pointerId) return
        if (!held.moved) {
            if (Math.hypot(event.data.global.x - held.start.x, event.data.global.y - held.start.y) < 6) return
            held.moved = true
            this.suppressed = held.source.node
            this.hideDetails()
            held.source.node.alpha = 0.4
            held.source.node.cursor = "grabbing"
            this.ghost = this.makeGhost(held.source.item!, held.source.size)
            this.ghost.alpha = 1
            this.ghost.interactive = false
            this.ghost.interactiveChildren = false
            this.layer.addChild(this.ghost)
        }
        const target = this.targetAt(event)
        const hoveredSlot = this.slotAt(event)
        this.primeSockets(hoveredSlot)
        this.slots.forEach(slot => slot.highlight(!!this.optionFor(slot.section, slot) && slot === hoveredSlot))
        this.sections.forEach(section => {
            // Area feedback stays steady across wells and gaps, even when only occupied
            // slots can accept the item (same-area replacements or a full ready bar) -- but not over a
            // socket that is turning the item away.
            const acceptsDrop = !!this.optionFor(section.section) || this.slots.some(slot =>
                slot.section === section.section && !!this.optionFor(section.section, slot))
            this.highlightSection(section, section.section === target && acceptsDrop && !this.refuses(hoveredSlot))
        })
        const at = this.layer.toLocal(event.data.global)
        this.ghost!.position.set(at.x - held.source.size / 2, at.y - held.source.size / 2)
    }

    // While a drag is up, every gear socket says what it thinks of the item: the one it fits is lit,
    // the others step back, and one under the pointer that won't take it says so. Dragging worn gear
    // OUT lights nothing -- it is on its way to the pack.
    private primeSockets = (hovered?: Slot) => {
        const source = this.held?.source
        const kind = source?.socket ? undefined : source?.item?.equipSlot
        this.slots.forEach(slot => {
            if (!slot.socket) return
            slot.socket.prime(
                slot === hovered && this.refuses(slot) && slot !== source ? "bad"
                    : kind && slot.socket.kind === kind ? "want"
                    : source?.item?.equipSlot ? "dim" : undefined,
            )
        })
    }

    private release = (event: InteractionEvent) => {
        if (!this.held || this.held.pointerId !== event.data.pointerId) return
        const target = this.targetAt(event)
        const hovered = this.slotAt(event)
        const source = this.held.source
        const item = source.item
        const option = this.held.moved && target ? this.optionFor(target, hovered) : undefined
        const at = target === "world" ? event.data.global.clone() : undefined
        // Let go over a socket that won't take it: the item goes back where it came from, visibly.
        const refused = this.held.moved && this.refuses(hovered)
        const ghost = refused ? this.ghost : undefined
        if (ghost) this.ghost = undefined
        this.cancel()
        if (option) this.select(item!, option, at)
        if (refused) {
            hovered!.socket!.reject?.()
            if (ghost) this.bounceBack(ghost, source)
        }
    }

    private bounceBack = (ghost: Container, source: Slot) => {
        const ticker = Ticker.shared
        if (!ticker.started || source.node.destroyed || !ghost.parent) {
            ghost.destroy({ children: true })
            return
        }
        const from = ghost.position.clone()
        const to = ghost.parent.toLocal(source.node.getGlobalPosition())
        let elapsed = 0
        const step = () => {
            elapsed += ticker.deltaMS
            const t = Math.min(1, elapsed / BOUNCE_MS)
            const eased = 1 - Math.pow(1 - t, 3)
            if (ghost.destroyed) {
                ticker.remove(step)
                return
            }
            ghost.position.set(from.x + (to.x - from.x) * eased, from.y + (to.y - from.y) * eased)
            if (t >= 1) {
                ticker.remove(step)
                ghost.parent?.removeChild(ghost)
                ghost.destroy({ children: true })
            }
        }
        ticker.add(step)
    }

    private highlightSection = (section: Section, on: boolean) => {
        if (section.highlighted === on) return
        section.highlighted = on
        section.highlight(on)
    }

    private cancelPointer = (event: InteractionEvent) => {
        if (this.held?.pointerId === event.data.pointerId) this.cancel()
    }

    cancel = () => {
        if (this.held) {
            this.held.source.node.alpha = 1
            this.held.source.node.cursor = "grab"
        }
        this.held = undefined
        this.slots.forEach(slot => {
            slot.highlight(false)
            slot.socket?.prime(undefined)
        })
        this.sections.forEach(section => this.highlightSection(section, false))
        this.ghost?.parent?.removeChild(this.ghost)
        this.ghost?.destroy({ children: true })
        this.ghost = undefined
        this.interaction.off("pointermove", this.move)
        this.interaction.off("pointerup", this.release)
        this.interaction.off("pointerupoutside", this.cancelPointer)
        this.interaction.off("pointercancel", this.cancelPointer)
        window.removeEventListener("blur", this.cancel)
    }
}
